import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, ArrowRight, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import AuthShell from "@/components/shared/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, orgTypeHomePath } from "@/lib/auth";
import { API_BASE_URL, extractApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [policiesLoading, setPoliciesLoading] = useState(false);
  const [policyLoadingSlug, setPolicyLoadingSlug] = useState("");
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);

  useEffect(() => {
    let mounted = true;

    const loadPolicies = async () => {
      if (!API_BASE_URL) return;
      setPoliciesLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/policies`);
        const rows = response?.data?.data || response?.data || [];
        if (!mounted) return;
        setPolicies(Array.isArray(rows) ? rows : []);
      } catch {
        // Keep login usable even if policies endpoint is temporarily unavailable
        if (mounted) setPolicies([]);
      } finally {
        if (mounted) setPoliciesLoading(false);
      }
    };

    loadPolicies();
    return () => {
      mounted = false;
    };
  }, []);

  const openPolicy = async (slug) => {
    if (!slug || !API_BASE_URL) return;
    setPolicyLoadingSlug(slug);
    try {
      const response = await axios.get(`${API_BASE_URL}/policies/${encodeURIComponent(slug)}`);
      const policy = response?.data?.data || response?.data || null;
      if (!policy) return;

      if (policy.document_url) {
        window.open(policy.document_url, "_blank", "noopener");
        return;
      }

      setSelectedPolicy(policy);
      setPolicyModalOpen(true);
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to load policy.");
    } finally {
      setPolicyLoadingSlug("");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      const displayName = u.name ? u.name.split(" ")[0] : u.email;
      toast.success(`Welcome, ${displayName}`);
      if (u.must_change_password) {
        toast.warning("Please update your temporary password from My Account.");
      }
      navigate(orgTypeHomePath(u.org_type));
    } catch (err) {
      const { message, details } = extractApiError(err);
      const status = err?.response?.status;
      if (status === 403) {
        toast.error("This account cannot sign in to the dashboard. Customers should use the mobile app.");
      } else if (status === 429) {
        const retryAfter =
          Number(details?.retry_after_seconds) ||
          Number(err?.response?.headers?.["retry-after"]) ||
          null;
        toast.error(
          retryAfter
            ? `Too many attempts. Try again in ${retryAfter} seconds.`
            : "Too many attempts. Please try again shortly.",
        );
      } else {
        toast.error(message || "Login failed. Please check your credentials.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Sign in to Reyansh"
      subtitle="Operate with confidence — multi-service emergency console."
      footer={
        <>
          New service? <span className="font-semibold text-slate-700 opacity-60">Request access (disabled)</span>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div className="space-y-1.5">
          <Label htmlFor="email">Work email</Label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email-input"
            required
          />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <span className="text-xs font-medium text-slate-500">Forgot?</span>
          </div>
          <div className="relative">
            <Input
              id="password"
              type={show ? "text" : "password"}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              required
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-slate-400 hover:text-slate-700"
              data-testid="toggle-password"
            >
              {show ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
        </div>
        <Button
          type="submit"
          disabled={loading}
          data-testid="login-submit-button"
          className="h-11 w-full text-white shadow-md shadow-slate-300/40 transition-all hover:shadow-lg hover:shadow-slate-300/50"
          style={{ background: "linear-gradient(135deg, #57534e, #334155)" }}
        >
          {loading ? <Loader2 className="animate-spin" size={16} /> : <>Sign in <ArrowRight size={16} className="ml-1.5" /></>}
        </Button>

        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Legal & Platform Policies</p>
          {/* <p className="mt-1 text-xs text-slate-500">Public links (no login required). PDF opens in a new tab; if no PDF exists, policy text is shown.</p> */}

          <div className="mt-2">
            {policiesLoading ? (
              <p className="text-xs text-slate-500">Loading policies...</p>
            ) : policies.length === 0 ? (
              <p className="text-xs text-slate-500">No public policies available right now.</p>
            ) : (
              <div className="flex flex-wrap items-center gap-y-1 text-sm text-slate-700">
                {policies.map((policy, idx) => (
                  <React.Fragment key={policy.slug}>
                    <button
                      type="button"
                      onClick={() => openPolicy(policy.slug)}
                      className="underline-offset-2 hover:underline disabled:opacity-60"
                      disabled={policyLoadingSlug === policy.slug}
                    >
                      {policyLoadingSlug === policy.slug ? "Opening..." : policy.title || policy.slug}
                    </button>
                    {idx < policies.length - 1 ? <span className="px-1 text-slate-500">.</span> : null}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>
      </form>

      <Dialog open={policyModalOpen} onOpenChange={setPolicyModalOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedPolicy?.title || "Policy"}</DialogTitle>
            <DialogDescription>
              {selectedPolicy?.updated_at ? `Updated ${new Date(selectedPolicy.updated_at).toLocaleString()}` : "Policy details"}
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[55vh] overflow-y-auto rounded-lg border border-slate-200 bg-white p-4">
            <pre className="whitespace-pre-wrap break-words text-sm text-slate-700">{selectedPolicy?.body || "No policy body available."}</pre>
          </div>
        </DialogContent>
      </Dialog>
    </AuthShell>
  );
}
